import BaseStore from 'customer-flows/stores/base-store'
import { type Awaitable } from 'customer-flows/utils/core'
import { context } from 'customer-flows/utils/core'
import IntlService from 'ember-intl/services/intl'
import { service } from '@ember/service'

export default class BookingFlowStoreClass extends BaseStore {
  @service intl: IntlService
  @context('program') program: Awaitable<BookingFlow>

  get initialSelectionDescription(): string | undefined {
    return this.program.value?.initialSelectionDescription
  }

  get overrideConfirmationScreenText(): string | undefined {
    return this.program.value?.overrideConfirmationScreenText
  }

  get overrideConfirmationScreenTitle(): string | undefined {
    return this.program.value?.overrideConfirmationScreenTitle
  }

  get bookingPortalOverrideDescription(): string | undefined {
    return this.program.value?.bookingPortalOverrideDescription
  }

  get bookingPortalOverrideLinkLabel(): string | undefined {
    return this.program.value?.bookingPortalOverrideLinkLabel
  }

  get bookingPortalShowConfirmation(): boolean | undefined {
    return this.program.value?.bookingPortalShowConfirmation
  }

  get promoCodeDisplay(): PromoCodeDisplay | undefined {
    return this.program.value?.promoCodeDisplay
  }

  get promoCodeLocation(): PromoCodeLocation | undefined {
    return this.program.value?.promoCodeLocation
  }

  get overridePromocodeLinkText(): string | undefined {
    return this.program.value?.overridePromocodeLinkText
  }

  get overrideFreesaleText(): string | undefined {
    return this.program.value?.overrideFreesaleText
  }

  get overrideTextTicketEach(): string | undefined {
    return this.program.value?.overrideTextTicketEach
  }

  get overrideSoldOutText(): string | undefined {
    return this.program.value?.overrideSoldOutText
  }

  get callToBookText(): string | undefined {
    return this.program.value?.overrideCallToBookText ?? this.intl.t('common.call-to-book')
  }

  get minShowLeftCutoff(): number | undefined {
    return this.program.value?.minShowLeftCutoff
  }

  get shouldShowPromoCodeInput(): boolean {
    return this.promoCodeDisplay !== 'NONE' && this.promoCodeLocation !== 'NONE'
  }

  get shouldShowPromoCodeInCartSummary(): boolean {
    return this.promoCodeDisplay !== 'NONE'
  }

  get showTicketSection(): boolean {
    return this.program.value?.showTicketSection ?? false
  }

  get ignoreBookingCutoff(): boolean {
    return this.program.value?.ignoreBookingCutoff ?? false
  }

  get disableAutoAddTicket(): boolean {
    return this.program.value?.disableAutoAddTicket ?? false
  }

  get disableCountdownClock (): boolean {
    return this.program.value?.disableCountdownClock ?? false
  }

  get enableMembershipRedemption(): boolean {
    return this.program.value?.enableMembershipRedemption ?? false
  }

  get hideDateTime(): boolean {
    return this.program.value?.hideDateTime ?? false
  }

  get bundleItems(): BundleItem[] {
    return this.program.value?.bundleItems ?? []
  }

  get ticketSelection(): string[] {
    return this.program.value?.ticketSelection ?? []
  }

  get providerName(): string | undefined {
    return this.program.value?.provider?.name
  }

  get smsEnabled(): boolean {
    return this.program.value?.smsConfig?.enabled ?? false
  }

  get smsOptinText(): string | undefined {
    return this.program.value?.smsConfig?.optinText
  }

  get smsTermsUrl(): string {
    return this.program.value?.smsConfig?.termsUrl ?? 'https://www.peekpro.com/customer-terms'
  }

  get smsPrivacyPolicyUrl(): string {
    return this.program.value?.smsConfig?.privacyPolicyUrl ?? 'https://www.peekpro.com/privacy-policy'
  }

  get calendarStartDate(): string | undefined {
    return this.program.value?.calendarStartDate
  }

  get optinMarketingEnabled(): boolean {
    return this.program.value?.optinMarketingEnabled ?? false
  }

  get optinMarketingTitle(): string | undefined {
    return this.program.value?.optinMarketingTitle
  }

  get optinMarketingDescription(): string | undefined {
    return this.program.value?.optinMarketingDescription
  }

  get timeSelectionMode(): TimeSelectionMode | undefined {
    return this.program.value?.timeSelectionMode
  }
}

declare global {
  export type BookingFlowStore = BookingFlowStoreClass
  export interface StoreRegistry {
    'program/booking-flow': BookingFlowStore
  }
}
